import { ZegoExpressEngine } from 'zego-express-engine-webrtc';
import ImCamera from './camera';

const INITIAL_TIMEOUT_MS = 30000;
const RECOVERY_TIMEOUT_MS = 10000;
const VIDEO_SWITCH_TIMEOUT_MS = 10000;
const READY_WHEN = Object.freeze(['local-published', 'remote-playing']);
const stop = stream => stream && stream.getTracks().forEach(track => track.stop());
const aborted = () => Object.assign(new Error('通话已结束'), { name: 'AbortError' });
const remotePlaybackError = errorCode => {
  const error = new Error('远端音视频播放失败');
  if (Number.isInteger(errorCode)) error.code = errorCode;
  return error;
};
const validId = (value, length) => typeof value === 'string' && value.length <= length && /^[A-Za-z0-9_-]+$/.test(value);

function checkCredentials(value) {
  if (!value || !validId(value.callId, 128) || !validId(value.roomId, 128)
    || !validId(value.userId, 64) || !validId(value.streamId, 256)
    || !Number.isInteger(value.appId) || value.appId < 1 || value.appId > 0xffffffff
    || typeof value.token !== 'string' || !/^04[A-Za-z0-9+/]+=*$/.test(value.token)
    || !Number.isFinite(value.expiresAt) || value.expiresAt <= Date.now() / 1000 + 5) {
    throw new Error('音视频凭证无效或已过期');
  }
  // 只保存公开初始化字段；不接收自定义 server 或业务源站 URL。
  const { callId, appId, roomId, userId, streamId, token, expiresAt } = value;
  return { callId, appId, roomId, userId, streamId, token, expiresAt };
}

/**
 * Web/Electron 唯一媒体会话。每通电话创建一次，关闭后不可复用。
 * 业务授权和 callId 仍由业务服务管理，本类不创建邀请或自行决定通话成员。
 * options: getCredentials(callId)、onLocalStream、onRemoteStream、onRemoteRemoved、
 * readyWhen 指定业务就绪条件：单聊要求远端播放，群聊只要求本地发布。
 * 依赖参数仅用于无云账号的生命周期回归。
 */
export default class RtcMediaSession {
  constructor(options = {}, dependencies = {}) {
    if (!READY_WHEN.includes(options.readyWhen)) throw new Error('媒体就绪条件无效');
    this.options = options;
    this.readyWhen = options.readyWhen;
    this.camera = dependencies.camera || new ImCamera();
    this.createCamera = dependencies.createCamera || (() => new ImCamera());
    this.videoCamera = null;
    this.pendingVideoCamera = null;
    this.switching = false;
    this.switchGeneration = 0;
    this.createEngine = dependencies.createEngine || (appId => new ZegoExpressEngine(appId, ''));
    this.engine = null;
    this.credentials = null;
    this.localStream = null;
    this.sdkStream = null;
    this.remote = new Map();
    this.handlers = [];
    this.closed = false;
    this.preparing = false;
    this.joining = false;
    this.phase = 'created';
    this.startedAt = Date.now();
    this.published = false;
    // 纯音频流切换到视频时，SDK 会先报告一次 NO_PUBLISH，再报告新的
    // PUBLISHING。该过渡属于正常换轨，不能把它当成通话失败。
    this.publishTransition = null;
    this.retiredSdkStreams = [];
    this.roomConnected = false;
    this.ready = false;
    this.readyReported = false;
    this.renewal = null;
    this.cancelRenewal = null;
    this.renewalTimer = null;
    this.expiryTimer = null;
    this.timer = null;
    // Vue 2 不观察不可扩展对象，防止 SDK/MediaStream 被深层响应式改写。
    Object.preventExtensions(this);
  }

  emit(event, ...args) {
    if (this.options[event]) this.options[event](...args);
  }

  diagnose(event, details = {}) {
    this.emit('onDiagnostic', {
      event,
      phase: this.phase,
      elapsedMs: Date.now() - this.startedAt,
      ...details
    });
  }

  async prepare(mode) {
    if (this.closed) throw aborted();
    if (!['voice', 'video'].includes(mode)) throw new Error('通话类型无效');
    if (this.preparing || this.localStream || this.joining) throw new Error('通话设备已在准备或使用中');
    this.preparing = true;
    this.phase = 'device-capture';
    this.diagnose('device-capture-started', { mode });
    try {
      if (!this.camera.isEnable()) throw new Error('当前环境不支持设备采集，请使用安全连接或桌面客户端');
      const stream = await (mode === 'video' ? this.camera.openVideo() : this.camera.openAudio());
      if (this.closed) { stop(stream); throw aborted(); }
      this.localStream = stream;
      this.phase = 'device-ready';
      this.diagnose('device-capture-ready', { trackKinds: stream.getTracks().map(track => track.kind) });
      stream.getTracks().forEach(track => {
        track.onended = () => {
          if (!this.closed && this.localStream?.getTracks().includes(track)) {
            this.fail(track.kind === 'audio' ? '麦克风采集中断，请检查设备连接与权限' : '摄像头采集中断，请检查设备连接与权限');
          }
        };
      });
      this.emit('onLocalStream', stream);
      return stream;
    } catch (error) {
      this.diagnose('device-capture-failed', { errorName: error && error.name || 'Error' });
      this.close();
      throw error;
    } finally {
      this.preparing = false;
    }
  }

  async join(value) {
    if (this.closed) throw aborted();
    if (!this.localStream) throw new Error('请先准备通话设备');
    if (this.joining) throw new Error('已在连接当前通话');
    this.joining = true;
    let failureMessage = '音视频凭证无效或已过期';
    try {
      failureMessage = '缺少通话续签入口';
      if (typeof this.options.getCredentials !== 'function') throw new Error('缺少通话续签入口');
      failureMessage = '音视频凭证无效或已过期';
      this.credentials = checkCredentials(value);
      this.armExpiry();
      this.phase = 'engine-check';
      this.diagnose('credentials-ready', {
        expiresInSeconds: Math.max(0, Math.round(this.credentials.expiresAt - Date.now() / 1000))
      });
      failureMessage = '音视频引擎初始化失败';
      const engine = this.createEngine(this.credentials.appId);
      this.engine = engine;
      engine.setDebugVerbose(false);
      engine.setLogConfig({ logLevel: 'disable', remoteLogLevel: 'disable' });
      this.listen();
      this.armTimeout(INITIAL_TIMEOUT_MS);
      failureMessage = '当前浏览器或桌面运行环境不支持音视频传输';
      // 不用默认的全能力检测；它会额外打开摄像头/麦克风。采集由 camera 唯一持有。
      const support = await engine.checkSystemRequirements('webRTC');
      if (this.closed) throw aborted();
      if (!support.result) throw new Error('当前浏览器或桌面运行环境不支持音视频传输');
      failureMessage = '本地媒体流注册失败';
      // 只把已采集的源注册给 SDK，不再申请一次硬件。后续轨道切换使用此注册流。
      const registered = await engine.createStream({ custom: { source: this.localStream } });
      if (this.closed) {
        try { engine.destroyStream(registered); } catch (_) { /* engine already destroyed */ }
        throw aborted();
      }
      this.sdkStream = registered;
      const c = this.credentials;
      failureMessage = '音视频房间连接失败';
      this.phase = 'room-login';
      this.diagnose('room-login-requested');
      const joined = await engine.loginRoom(c.roomId, c.token, { userID: c.userId, userName: c.userId }, { userUpdate: true });
      if (this.closed) {
        // 退出发生于登录请求期间时，也清理由迟到成功响应建立的云房间连接。
        try { engine.logoutRoom(c.roomId); } catch (_) { /* engine already destroyed */ }
        try { engine.destroyEngine(); } catch (_) { /* continue cancellation */ }
        throw aborted();
      }
      if (!joined) throw new Error('音视频房间连接失败');
      this.roomConnected = true;
      this.phase = 'publishing';
      this.diagnose('room-login-succeeded');
      failureMessage = '音视频发布失败';
      const publishing = engine.startPublishingStream(c.streamId, this.sdkStream, { roomID: c.roomId });
      this.diagnose('publish-requested', { accepted: Boolean(publishing) });
      if (!publishing) {
        throw new Error('音视频发布失败');
      }
    } catch (error) {
      const wasClosed = this.closed;
      this.diagnose('join-failed', {
        errorName: error && error.name || 'Error',
        errorCode: Number.isInteger(error && (error.errorCode || error.code)) ? (error.errorCode || error.code) : null
      });
      this.close();
      if (wasClosed || (error && error.name === 'AbortError')) throw aborted();
      const safeError = new Error(failureMessage);
      const code = error && (error.errorCode || error.code);
      if (Number.isInteger(code)) safeError.code = code;
      throw safeError;
    }
  }

  listen() {
    const engine = this.engine;
    const roomId = this.credentials.roomId;
    const on = (event, callback) => {
      const handler = (...args) => { if (!this.closed) callback(...args); };
      this.handlers.push([event, handler]);
      engine.on(event, handler);
    };
    on('tokenWillExpire', room => {
      if (room !== roomId) return;
      this.diagnose('token-will-expire');
      this.renew();
    });
    on('roomStateChanged', (room, reason, errorCode) => {
      if (room === roomId) this.diagnose('room-state-changed', { reason, errorCode: Number.isInteger(errorCode) ? errorCode : null });
      if (room === roomId && ['KICKOUT', 'LOGIN_FAILED', 'RECONNECT_FAILED'].includes(reason)) {
        this.fail('通话授权已结束或房间连接失败，请重新发起通话', errorCode);
      }
    });
    on('roomStateUpdate', (room, state, errorCode) => {
      if (room !== roomId) return;
      this.roomConnected = state === 'CONNECTED';
      this.diagnose('room-state', { state, errorCode: Number.isInteger(errorCode) ? errorCode : null });
      if (state === 'DISCONNECTED') {
        this.reconnecting();
      }
      else if (state === 'CONNECTING') this.reconnecting();
      else this.checkReady();
      // 登录成功仅代表房间信令就绪，不能据此开始通话计时。
    });
    on('publisherStateUpdate', result => {
      if (result.streamID !== this.credentials.streamId) return;
      this.published = result.state === 'PUBLISHING';
      this.diagnose('publisher-state', { state: result.state, errorCode: Number.isInteger(result.errorCode) ? result.errorCode : null });
      if (result.state === 'NO_PUBLISH') {
        if (this.publishTransition) {
          this.published = false;
          this.diagnose('publisher-transition', { state: result.state });
        } else {
          this.fail('本地音视频发布失败', result.errorCode);
        }
      }
      else if (!this.published) this.reconnecting();
      if (result.state === 'PUBLISHING' && this.publishTransition) {
        const transition = this.publishTransition;
        this.publishTransition = null;
        transition.resolve();
      }
      this.checkReady();
    });
    on('roomStreamUpdate', (room, action, streams) => {
      if (room !== roomId) return;
      for (const info of streams) {
        if (this.closed) return;
        if (info.user.userID === this.credentials.userId) continue;
        if (action === 'ADD') this.subscribe(info);
        else if (action === 'DELETE') this.removeRemote(info.streamID);
      }
    });
    on('playerStateUpdate', result => {
      const entry = this.remote.get(result.streamID);
      if (!entry) return;
      entry.playing = result.state === 'PLAYING';
      this.diagnose('player-state', { state: result.state, errorCode: Number.isInteger(result.errorCode) ? result.errorCode : null });
      if (result.state === 'NO_PLAY') {
        this.diagnose('remote-playback-failed', { streamId: result.streamID, errorCode: result.errorCode });
        this.removeRemote(result.streamID);
        const error = remotePlaybackError(result.errorCode);
        if (this.readyWhen === 'remote-playing') this.fail(error.message, result.errorCode);
        else this.emit('onRemoteError', error, result.streamID, result.errorCode);
      } else if (!entry.playing && this.readyWhen === 'remote-playing') this.reconnecting();
      this.checkReady();
    });
  }

  async subscribe(info) {
    const id = info.streamID;
    if (this.remote.has(id)) return;
    const entry = { info, stream: null, playing: false };
    this.remote.set(id, entry);
    try {
      const stream = await this.engine.startPlayingStream(id, { roomID: this.credentials.roomId });
      if (this.closed || this.remote.get(id) !== entry) { stop(stream); return; }
      entry.stream = stream;
      this.emit('onRemoteStream', info.user.userID, stream, id);
      this.diagnose('remote-stream-ready');
      this.checkReady();
    } catch (error) {
      if (!this.closed && this.remote.get(id) === entry) {
        this.diagnose('remote-stream-failed', {
          streamId: id,
          errorCode: Number.isInteger(error && error.errorCode) ? error.errorCode : null
        });
        this.removeRemote(id);
        const remoteError = remotePlaybackError(error && error.errorCode);
        if (this.readyWhen === 'remote-playing') this.fail(remoteError.message, error && error.errorCode);
        else this.emit('onRemoteError', remoteError, id, error && error.errorCode);
      }
    }
  }

  removeRemote(id) {
    const entry = this.remote.get(id);
    if (!entry) return;
    this.remote.delete(id);
    this.engine.stopPlayingStream(id);
    this.emit('onRemoteRemoved', entry.info.user.userID, id);
    if (this.readyWhen === 'remote-playing' && this.remote.size === 0) this.reconnecting();
    // 群成员是否离开/通话是否结束由业务会话决定，不在这里创建第二套成员状态。
  }

  checkReady() {
    if (this.closed || this.ready || !this.published || !this.roomConnected) return;
    if (this.readyWhen === 'remote-playing' && ![...this.remote.values()].some(entry => entry.playing && entry.stream)) return;
    clearTimeout(this.timer);
    this.timer = null;
    this.ready = true;
    this.phase = 'media-ready';
    this.diagnose('media-ready');
    this.emit('onState', 'connected');
    if (!this.readyReported) {
      this.readyReported = true;
      this.emit('onMediaReady');
    }
  }

  reconnecting() {
    if (this.closed || !this.ready) return;
    this.ready = false;
    this.phase = 'reconnecting';
    this.diagnose('media-reconnecting');
    this.emit('onState', 'reconnecting');
    this.armTimeout(RECOVERY_TIMEOUT_MS);
  }

  armTimeout(milliseconds) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.fail('音视频连接超时，请检查网络或VPN后重试');
    }, milliseconds);
  }

  setMicrophone(enabled) {
    if (this.closed || !this.sdkStream || !this.localStream) throw new Error('通话尚未连接');
    if (!this.engine.mutePublishStreamAudio(this.sdkStream, !enabled)) throw new Error('麦克风状态切换失败');
    this.localStream.getAudioTracks().forEach(track => { track.enabled = enabled; });
  }

  async republishWithVideoTrack(track, previousSdkStream, kind) {
    if (!this.engine || !this.credentials || !this.localStream || typeof MediaStream === 'undefined') {
      throw new Error('视频轨道切换失败');
    }
    const source = new MediaStream([...this.localStream.getAudioTracks(), track]);
    const replacement = await this.engine.createStream({ custom: { source } });
    if (this.closed) {
      try { this.engine.destroyStream(replacement); } catch (_) { /* continue cancellation */ }
      throw aborted();
    }
    const streamId = this.credentials.streamId;
    const roomID = this.credentials.roomId;
    const transition = {};
    transition.promise = new Promise((resolve, reject) => {
      transition.resolve = resolve;
      transition.reject = reject;
      transition.timer = setTimeout(() => {
        if (this.publishTransition === transition) this.publishTransition = null;
        reject(new Error('视频发布切换超时'));
      }, VIDEO_SWITCH_TIMEOUT_MS);
    });
    this.publishTransition = transition;
    if (!this.engine.stopPublishingStream(streamId)) {
      clearTimeout(transition.timer);
      this.publishTransition = null;
      try { this.engine.destroyStream(replacement); } catch (_) { /* continue cleanup */ }
      throw new Error('视频轨道切换失败');
    }
    if (!this.engine.startPublishingStream(streamId, replacement, { roomID })) {
      clearTimeout(transition.timer);
      this.publishTransition = null;
      try { this.engine.destroyStream(replacement); } catch (_) { /* continue cleanup */ }
      throw new Error('视频轨道切换失败');
    }
    try {
      await transition.promise;
    } catch (error) {
      clearTimeout(transition.timer);
      if (this.publishTransition === transition) this.publishTransition = null;
      try { this.engine.destroyStream(replacement); } catch (_) { /* continue cleanup */ }
      throw error;
    }
    clearTimeout(transition.timer);
    this.sdkStream = replacement;
    this.published = true;
    // replacement 与旧流共享麦克风轨道。延迟销毁旧 SDK 流到整个会话结束，
    // 避免 SDK destroyStream 把仍在使用的麦克风轨道一并停止。
    if (previousSdkStream && previousSdkStream !== replacement) {
      this.retiredSdkStreams.push(previousSdkStream);
    }
    this.diagnose('video-track-republished', { kind });
    return { errorCode: 0 };
  }

  /** 只替换视频轨，麦克风始终由最初采集器持有；取消选择不会丢失原画面。 */
  async switchVideo(kind) {
    if (this.closed || !this.sdkStream) throw aborted();
    if (!['camera', 'screen', 'off'].includes(kind)) throw new Error('视频设备类型无效');
    if (this.switching) throw new Error('设备正在切换，请稍候');
    this.switching = true;
    const switchGeneration = ++this.switchGeneration;
    this.diagnose('video-switch-started', { kind });
    const engine = this.engine;
    const sdkStream = this.sdkStream;
    let next = null;
    let preview = null;
    try {
      let track = null;
      if (kind !== 'off') {
        next = this.createCamera();
        this.pendingVideoCamera = next;
        const source = await (kind === 'screen' ? next.openScreen(false) : next.openVideoOnly());
        if (this.closed) throw aborted();
        track = source.getVideoTracks()[0];
        if (!track) throw new Error('未获取到视频画面');
        this.diagnose('video-device-ready', { kind });
      }
      // 本地设备轨道已经拿到即可预览，不等待 ZEGO 的换轨/重新发布完成。
      // 这样本地画面反馈与云端媒体切换解耦，云端慢时也不会把本地按钮卡在黑屏。
      preview = new MediaStream([...this.localStream.getAudioTracks(), ...(track ? [track] : [])]);
      this.emit('onLocalStream', preview);
      const oldVideoTracks = sdkStream.getVideoTracks();
      let result;
      if (track && !oldVideoTracks.length) {
        // ZEGO 3.13.0 的 addTrack 在纯音频发布流上会读取未定义的
        // videoInfo，并在 SDK 内部抛出 updateVideoInfo(undefined)。设备
        // 采集已经成功时直接重建发布流，避免触发这条有问题的 SDK 路径。
        this.diagnose('video-track-republish-required', { kind });
        result = await this.republishWithVideoTrack(track, sdkStream, kind);
      } else {
        const switchOperation = track
          ? engine.replaceTrack(sdkStream, track)
          : (oldVideoTracks.length ? engine.removeTrack(sdkStream, oldVideoTracks[0]) : null);
        if (switchOperation && typeof switchOperation.then === 'function') {
          let timeoutId;
          try {
            result = await Promise.race([
              switchOperation,
              new Promise((resolve, reject) => {
                timeoutId = setTimeout(() => reject(Object.assign(new Error('摄像头切换超时，请检查摄像头和权限'), { name: 'VideoSwitchTimeoutError' })), VIDEO_SWITCH_TIMEOUT_MS);
              })
            ]);
          } finally {
            clearTimeout(timeoutId);
          }
        } else {
          result = switchOperation;
        }
      }
      if (this.closed || this.switchGeneration !== switchGeneration) throw aborted();
      if (result && Number.isInteger(result.errorCode) && result.errorCode !== 0) throw new Error('视频轨道切换失败');
      this.diagnose('video-track-switched', { kind, hasVideoTrack: Boolean(track) });
      oldVideoTracks.forEach(old => old.stop());
      if (this.videoCamera) this.videoCamera.close();
      this.videoCamera = next;
      this.pendingVideoCamera = null;
      next = null;
      this.localStream = preview;
      if (track) track.onended = () => {
        if (!this.closed && this.localStream === preview) this.emit('onVideoEnded', kind);
      };
      this.emit('onLocalStream', preview);
    } catch (error) {
      this.diagnose('video-switch-failed', {
        kind,
        errorName: error && error.name || 'Error',
        errorCode: Number.isInteger(error && (error.errorCode || error.code)) ? (error.errorCode || error.code) : null
      });
      if (next) next.close();
      if (preview && this.localStream !== preview && !this.closed) this.emit('onLocalStream', this.localStream);
      if (error && error.name === 'VideoSwitchTimeoutError' && this.switchGeneration === switchGeneration) {
        this.switchGeneration++;
        this.switching = false;
      }
      if (this.closed) throw aborted();
      throw error;
    } finally {
      if (this.switchGeneration === switchGeneration) {
        this.pendingVideoCamera = null;
        this.switching = false;
      }
    }
  }

  renew() {
    if (this.closed || !this.credentials || !this.engine) return Promise.resolve();
    if (this.renewal) return this.renewal;
    const current = this.credentials;
    const renewalStartedAt = Date.now();
    this.diagnose('token-renewal-started');
    this.renewal = new Promise((resolve, reject) => {
      this.cancelRenewal = () => reject(aborted());
      this.renewalTimer = setTimeout(() => reject(new Error('通话续签超时')), RECOVERY_TIMEOUT_MS);
      Promise.resolve().then(() => {
        if (this.closed) throw aborted();
        return this.options.getCredentials(current.callId);
      }).then(resolve, reject);
    }).then(value => {
      if (this.closed) return;
      const next = checkCredentials(value);
      for (const key of ['callId', 'appId', 'roomId', 'userId', 'streamId']) {
        if (next[key] !== current[key]) throw new Error('通话凭证上下文已改变');
      }
      if (!this.engine.renewToken(next.token, next.roomId)) throw new Error('音视频凭证续签失败');
      this.credentials = next;
      this.armExpiry();
      this.diagnose('token-renewal-succeeded', {
        durationMs: Date.now() - renewalStartedAt,
        expiresInSeconds: Math.max(0, Math.round(next.expiresAt - Date.now() / 1000))
      });
    }).catch(error => {
      this.diagnose('token-renewal-failed', {
        durationMs: Date.now() - renewalStartedAt,
        errorName: error && error.name || 'Error',
        errorCode: Number.isInteger(error && (error.errorCode || error.code)) ? (error.errorCode || error.code) : null
      });
      if (!this.closed) this.fail('通话授权已失效，请重新发起通话');
    }).finally(() => {
      clearTimeout(this.renewalTimer);
      this.renewalTimer = null;
      this.cancelRenewal = null;
      this.renewal = null;
    });
    return this.renewal;
  }

  armExpiry() {
    clearTimeout(this.expiryTimer);
    const remaining = this.credentials.expiresAt * 1000 - Date.now();
    // 即使 SDK 未通知续签，也不能让本地媒体无限运行。真正撤权仍由服务端执行。
    this.expiryTimer = setTimeout(() => {
      this.phase = 'credentials-expired';
      this.diagnose('local-credential-expired');
      this.fail('通话凭证已过期，请重新发起通话');
    },
      Math.max(0, Math.min(remaining, 0x7fffffff)));
  }

  fail(message, errorCode) {
    if (this.closed) return;
    this.diagnose('media-failed', { errorCode: Number.isInteger(errorCode) ? errorCode : null });
    const error = new Error(message);
    // 只携带数值错误码，不将 SDK 原始对象（可能含连接凭证）传播到界面或日志。
    if (Number.isInteger(errorCode)) error.code = errorCode;
    this.close();
    this.emit('onError', error);
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    this.switchGeneration++;
    this.switching = false;
    clearTimeout(this.timer);
    this.timer = null;
    clearTimeout(this.expiryTimer);
    this.expiryTimer = null;
    clearTimeout(this.renewalTimer);
    this.renewalTimer = null;
    if (this.cancelRenewal) this.cancelRenewal();
    const engine = this.engine;
    // 某项 SDK 清理失败不能阻止其他流、设备或定时器被释放。
    const safely = action => { try { action(); } catch (_) { /* continue cleanup */ } };
    if (engine) {
      this.handlers.forEach(([event, handler]) => safely(() => engine.off(event, handler)));
      if (this.credentials) safely(() => engine.stopPublishingStream(this.credentials.streamId));
      this.remote.forEach((_, id) => safely(() => engine.stopPlayingStream(id)));
      if (this.credentials) safely(() => engine.logoutRoom(this.credentials.roomId));
      if (this.sdkStream) safely(() => engine.destroyStream(this.sdkStream));
      this.retiredSdkStreams.forEach(stream => safely(() => engine.destroyStream(stream)));
      safely(() => engine.destroyEngine());
    }
    safely(() => this.camera.close());
    if (this.videoCamera) safely(() => this.videoCamera.close());
    if (this.pendingVideoCamera) safely(() => this.pendingVideoCamera.close());
    this.remote.clear();
    this.handlers = [];
    this.localStream = null;
    this.sdkStream = null;
    this.retiredSdkStreams = [];
    if (this.publishTransition) {
      clearTimeout(this.publishTransition.timer);
      this.publishTransition.reject(aborted());
      this.publishTransition = null;
    }
    this.engine = null;
    this.credentials = null;
  }
}
